/**
 * GoogleCallback — handles the OAuth redirect from Google.
 *
 * Google redirects here with ?code=<auth_code>&scope=...
 * We extract the code, send it to the backend for token exchange,
 * then redirect the user back to /dashboard/email-insights.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveGmailToken, reportOAuthBlock } from '../../modules/email-insights/engine/backendClient';
import { OAUTH_STATE_KEY, OAUTH_CLIENT_KEY_STORAGE } from '../../modules/email-insights/constants';

const GoogleCallback = () => {
  const navigate = useNavigate();
  const processed = useRef(false);
  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'
  const [error, setError] = useState(null);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const returnedState = params.get('state');
    const errorParam = params.get('error');

    // The nonce is single-use: read it once and clear it immediately, so a
    // replayed callback URL cannot be exchanged a second time.
    let expectedState = null;
    let oauthClientKey = null;
    try {
      expectedState = sessionStorage.getItem(OAUTH_STATE_KEY);
      oauthClientKey = sessionStorage.getItem(OAUTH_CLIENT_KEY_STORAGE);
      sessionStorage.removeItem(OAUTH_STATE_KEY);
      sessionStorage.removeItem(OAUTH_CLIENT_KEY_STORAGE);
    } catch {
      // Storage unavailable — expectedState stays null and the check below fails closed.
    }

    if (errorParam) {
      const desc = `${errorParam} ${params.get('error_description') || ''}`.toLowerCase();
      const googleClosedSlot = /block|unverified|sensitive|admin_policy/.test(desc);
      if (googleClosedSlot && oauthClientKey && /^ergode-[1-4]$/.test(oauthClientKey)) {
        reportOAuthBlock(oauthClientKey, desc.slice(0, 180)).catch(() => {});
      }
      setError(errorParam);
      setStatus('error');
      setTimeout(() => navigate('/dashboard/email-insights'), 3000);
      return;
    }

    if (!code) {
      setError('No authorization code received from Google.');
      setStatus('error');
      setTimeout(() => navigate('/dashboard/email-insights'), 3000);
      return;
    }

    // CSRF check (RFC 6749 §10.12) — only exchange a code whose state matches
    // the nonce this tab minted at connect(). Fails closed on any mismatch.
    if (!expectedState || !returnedState || returnedState !== expectedState) {
      setError('Security check failed — this sign-in did not start in this browser. Please try connecting Gmail again.');
      setStatus('error');
      setTimeout(() => navigate('/dashboard/email-insights'), 4000);
      return;
    }

    saveGmailToken(code, oauthClientKey)
      .then(() => {
        setStatus('success');
        setTimeout(() => navigate('/dashboard/email-insights', { replace: true }), 2000);
      })
      .catch((err) => {
        setError(err.message || 'Failed to connect Gmail. Please try again.');
        setStatus('error');
        setTimeout(() => navigate('/dashboard/email-insights'), 3000);
      });
  }, [navigate]);

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#f9fafb' }}>
      <div style={{ textAlign: 'center', maxWidth: 400, padding: 32 }}>
        {status === 'error' && (
          <>
            <div style={{
              width: 48, height: 48, borderRadius: '50%', background: '#fef2f2',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px'
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </div>
            <p style={{ color: '#ef4444', fontSize: 16, fontWeight: 500, marginBottom: 8 }}>{error}</p>
            <p style={{ color: '#6b7280', fontSize: 14 }}>Redirecting back to Email Insights...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div style={{
              width: 48, height: 48, borderRadius: '50%', background: '#f0fdf4',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px'
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <p style={{ color: '#111827', fontSize: 18, fontWeight: 600, marginBottom: 6 }}>Google account connected successfully!</p>
            <p style={{ color: '#6b7280', fontSize: 14 }}>Redirecting to Email Insights...</p>
          </>
        )}

        {status === 'loading' && (
          <>
            <div style={{
              width: 40, height: 40, border: '3px solid #e5e7eb', borderTopColor: '#00D1C1',
              borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px'
            }} />
            <p style={{ color: '#111827', fontSize: 16, fontWeight: 500 }}>Connecting your Gmail account...</p>
            <p style={{ color: '#6b7280', fontSize: 14, marginTop: 4 }}>Please wait while we complete the setup.</p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </>
        )}
      </div>
    </div>
  );
};

export default GoogleCallback;
